/* ═══ Newborn Cart enhancements ═══
 * Self-contained, resilient cart behaviors:
 *   - Auto-adds the free bag when subtotal crosses the bag threshold
 *   - Dynamically resolves the bag variant ID via /products/{handle}.js
 *     (so we don't hard-code a variant that might change)
 *   - Refreshes cart sections via the Sections API (no full page reload)
 *   - Fires a confetti burst + banner when a tier is newly unlocked
 *   - Respects user removal (session flag) so we don't re-add behind their back
 *
 * SHOPIFY DISCOUNTS THAT BACK THESE GIFTS
 *   Both gifts are rewards of the octogun-promo Shopify Function (app
 *   "Discounts", automatic discount 1669661393253), alongside the case BOGO.
 *   They were BXGYs until 18 Aug 2026; the two BXGYs are now deactivated.
 *
 * IF YOU ADD A GIFT TIER HERE, CREATE ITS DISCOUNT FIRST. A tier with no live
 * discount behind it auto-adds the gift and charges for it. That exact bug ran
 * on the 6-tube gun trigger until 6 Aug 2026.
 *
 * NEVER CALL A LINE FREE BECAUSE THE CART CLEARED A THRESHOLD. A threshold says
 * what should happen; only the line's own allocation says what will be charged.
 * The two part company whenever something outranks our gift rewards:
 *
 *   - A discount code whose combinesWith.productDiscounts is false (OCTO40)
 *     suppresses the WHOLE function, so every gift reward disappears at once.
 *     Shopify keeps whichever deal is worth more to the customer, so their
 *     total is still the best available - but a gift we auto-added silently
 *     turns into a line they pay for.
 *   - A backing discount that expires does the same thing more quietly.
 *
 * So every "FREE" painted below is gated on lineIsZeroed(), and any gift WE
 * added that is not zeroed gets pulled back out. Threshold checks decide what
 * to offer; allocations decide what to promise.
 */
(function () {
  'use strict';

  const BAG_HANDLE = 'a-newbornbag';
  const SILICONE_HANDLE = 'm-ap-white';
  const SILICONE_GIFT_PROP = 'free-silicone-cartridge';
  const SHIP_THRESHOLD = 2500; // cents
  const BAG_THRESHOLD = 3500;  // cents

  // Free Model 930-GTD at $300 of order value.
  //
  // Dollar threshold, not case counts. Case values swing from $114 to $174 by
  // SKU, so dollar tiers are SKU-agnostic. Locked in COMPANY-REFERENCE
  // "Free-goods ladder (dollar thresholds, not case counts)": every tier costs
  // under 2 percent of order value. At $300 the 930-GTD is 1.5 percent.
  //
  // Replaced the old quantity trigger (6 loose tubes -> 1 gun, packs -> 1..20
  // guns) on 6 Aug 2026. That trigger kept auto-adding guns after all six of
  // its backing BXGYs expired, so the cart charged $14.99 a gun while badging
  // the line FREE. Never reintroduce a quantity-based gun tier.
  const FREE_GUN_HANDLE = '930-gtd';
  const FREE_GUN_VARIANT = 50212134748517; // Model 930-GTD default variant
  const FREE_GUN_PROP = 'free-gun-300';
  const FREE_GUN_PROP_LEGACY = 'free-gun-6silicone'; // still in live carts, recognise so we can clear it
  const GUN_THRESHOLD = 30000; // cents

  const STORAGE_USER_REMOVED = 'nb_bag_user_removed';
  const STORAGE_USER_REMOVED_GUN = 'nb_gun_user_removed';
  const STORAGE_LAST_TIER = 'nb_cart_last_tier';
  // Remembers that the discount engine refused to zero our gifts, so we stop
  // re-adding a gift the shopper would be charged for. Keyed by the applicable
  // discount codes, so dropping the code makes the flag stale and the gift
  // comes straight back. See giftsBlocked().
  const STORAGE_GIFT_BLOCKED = 'nb_gift_blocked_by';
  const DEBUG = false;

  function log() { if (DEBUG) console.log.apply(console, ['[nb-cart]'].concat([].slice.call(arguments))); }
  function warn() { console.warn.apply(console, ['[nb-cart]'].concat([].slice.call(arguments))); }

  // --- fetch helpers ---
  async function jget(url) {
    const r = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(url + ' -> ' + r.status);
    return r.json();
  }

  async function getCart() {
    return jget('/cart.js');
  }

  // Toggle a body class whenever the cart contains a product from the
  // Octogun collection. Used by CSS to apply red accents on the cart page
  // and cart drawer. Product handles are emitted server-side via
  // window.NB_OCTOGUN_HANDLES in layout/theme.liquid.
  function updateOctogunBodyClass(cart) {
    try {
      const handles = Array.isArray(window.NB_OCTOGUN_HANDLES)
        ? window.NB_OCTOGUN_HANDLES
        : [];
      if (!handles.length) return;
      const hasOcto = (cart.items || []).some((i) => {
        const h =
          (i.handle && i.handle) ||
          (i.product && i.product.handle) ||
          '';
        return h && handles.indexOf(h) !== -1;
      });
      document.body.classList.toggle('nb-cart-has-octogun', hasOcto);
    } catch (e) {
      /* noop */
    }
  }

  // Visually mark the bag line as FREE on the cart page + cart drawer.
  // Why: Shopify's BXGY automatic discount only applies at checkout, so the
  // /cart page shows the bag at its full $9.99 price even when it qualifies
  // to be free. This overlays the same "$0.00 + strikethrough" treatment the
  // drawer shows when Shopify itself applies the discount.
  //
  // --- gift reality checks ---

  // Did the discount engine actually zero this line? This is the only honest
  // basis for the word FREE. `line_price` / `final_line_price` are the price
  // AFTER discounts (0 on a free line); `original_line_price` is before.
  function lineIsZeroed(line) {
    if (!line) return false;
    const gross =
      line.original_line_price != null
        ? line.original_line_price
        : (line.original_price || line.price || 0) * (line.quantity || 1);
    if (!gross) return true; // a genuinely $0 product is free either way
    const net =
      line.final_line_price != null ? line.final_line_price : line.line_price;
    return net === 0;
  }

  // What the line would cost undiscounted, for the strikethrough. Must not read
  // line_price: that is already 0 once the gift is free, which printed a
  // struck-through "$0.00" next to FREE.
  function grossLinePrice(line) {
    if (!line) return 0;
    return line.original_line_price != null
      ? line.original_line_price
      : (line.original_price || line.price || 0) * (line.quantity || 1);
  }

  // The applicable codes on the cart, as a stable key. Lets us remember a
  // suppressed gift without remembering it forever: change or drop the code and
  // the key changes, the flag goes stale, and the gift is offered again.
  function applicableCodeKey(cart) {
    return (((cart || {}).discount_codes) || [])
      .filter((c) => c && c.applicable)
      .map((c) => c.code)
      .sort()
      .join('|');
  }

  function giftsBlocked(cart) {
    try {
      return (
        sessionStorage.getItem(STORAGE_GIFT_BLOCKED) === 'k:' + applicableCodeKey(cart)
      );
    } catch (e) {
      return false;
    }
  }

  function setGiftsBlocked(cart) {
    try {
      sessionStorage.setItem(STORAGE_GIFT_BLOCKED, 'k:' + applicableCodeKey(cart));
    } catch (e) {
      /* sessionStorage unavailable — we just re-check next evaluation */
    }
  }

  // Are the function's gift rewards suppressed on this cart? Two ways to know:
  //
  //   REMEMBERED - we already pulled a gift of ours for these codes.
  //   OBSERVED   - a gift line is sitting here unzeroed even though the cart
  //                clears that tier, with a code applied.
  //
  // The observed half is what covers a bag the shopper added themselves. We
  // leave their line alone (it is theirs, not ours to remove), but the ladder
  // must stop calling it free.
  function giftRewardsSuppressed(cart) {
    if (giftsBlocked(cart)) return true;
    if (!applicableCodeKey(cart)) return false;
    const grants = giftGrants(nonGiftSubtotal(cart));
    const lineFor = (handle) =>
      ((cart || {}).items || []).find(
        (i) => (i.handle || (i.product && i.product.handle)) === handle
      );
    const bag = lineFor(BAG_HANDLE);
    if (grants.bag && bag && !lineIsZeroed(bag)) return true;
    const gun = lineFor(FREE_GUN_HANDLE);
    if (grants.gun && gun && !lineIsZeroed(gun)) return true;
    return false;
  }

  // Undo a FREE painting when the line stopped being free. A section re-render
  // wipes our markup anyway; this covers the in-place path, where the row
  // survives and only the price changed underneath it.
  function unpaintFreeRows(handle, flagKey) {
    try {
      document
        .querySelectorAll('.cart-items__table-row, [data-key]')
        .forEach((row) => {
          if (!row.querySelector('a[href*="/products/' + handle + '"]')) return;
          if (row.dataset[flagKey] !== '1') return;
          delete row.dataset[flagKey];
          row.classList.remove('nb-cart-free-bag');
          ['.cart-items__price', '.cart-items__unit-price-wrapper'].forEach((sel) => {
            const cell = row.querySelector(sel);
            if (cell && cell.dataset.nbOriginal != null) {
              cell.innerHTML = cell.dataset.nbOriginal;
              delete cell.dataset.nbOriginal;
            }
          });
          const badge = row.querySelector('.nb-free-bag-badge');
          if (badge) badge.remove();
        });
    } catch (e) {
      /* noop — purely visual */
    }
  }

  // Safe when cart has items from BOTH the main store and the Octogun side —
  // we qualify on total non-bag subtotal, not category.
  function markFreeBagInDOM(cart) {
    try {
      if (!cart || !Array.isArray(cart.items)) return;
      const bagItem = cart.items.find(
        (i) =>
          (i.handle && i.handle === BAG_HANDLE) ||
          (i.product && i.product.handle === BAG_HANDLE)
      );
      if (!bagItem) return;

      // Paint FREE only when the line is genuinely zeroed. The old test asked
      // whether the cart CLEARED the bag threshold, which stays true even when
      // a discount code has suppressed the function, and that is exactly what
      // put a FREE badge on a bag checkout then charged $9.99 for.
      const bagPrice = grossLinePrice(bagItem);
      if (!lineIsZeroed(bagItem)) {
        unpaintFreeRows(BAG_HANDLE, 'nbFreeBagMarked');
        return;
      }

      // Find all cart rows that reference the bag product. Works on both the
      // /cart page (cart-products.liquid <tr>) and the cart drawer (same
      // template, rendered inside a <cart-drawer-component>).
      const rows = document.querySelectorAll(
        '.cart-items__table-row, [data-key]'
      );
      rows.forEach((row) => {
        const link = row.querySelector('a[href*="/products/' + BAG_HANDLE + '"]');
        if (!link) return;
        if (row.dataset.nbFreeBagMarked === '1') return;
        row.dataset.nbFreeBagMarked = '1';
        row.classList.add('nb-cart-free-bag');

        // Replace the line total cell
        const priceCell = row.querySelector('.cart-items__price');
        if (priceCell && !priceCell.querySelector('.nb-free-bag-price')) {
          const original = priceCell.innerHTML;
          priceCell.innerHTML =
            '<div class="nb-free-bag-price">' +
              '<span class="nb-free-bag-free">FREE</span>' +
              '<s class="nb-free-bag-was">' + formatMoney(bagPrice) + '</s>' +
            '</div>';
          priceCell.dataset.nbOriginal = original;
        }

        // Replace the unit price in the details cell too
        const unit = row.querySelector('.cart-items__unit-price-wrapper');
        if (unit && !unit.querySelector('.nb-free-bag-unit')) {
          unit.dataset.nbOriginal = unit.innerHTML;
          unit.innerHTML =
            '<div class="nb-free-bag-unit">' +
              '<span class="nb-free-bag-free">$0.00</span>' +
              '<s class="nb-free-bag-was">' + formatMoney(bagPrice) + '</s>' +
            '</div>';
        }

        // Badge under the title
        const titleBlock = row.querySelector('.cart-items__product-info');
        if (titleBlock && !titleBlock.querySelector('.nb-free-bag-badge')) {
          const badge = document.createElement('span');
          badge.className = 'nb-free-bag-badge';
          badge.textContent = 'FREE GIFT · $35+ ORDER';
          titleBlock.appendChild(badge);
        }
      });
    } catch (e) {
      /* noop — purely visual */
    }
  }

  // Paint the auto-added free 930-GTD line as FREE in the cart (mirrors the bag).
  // Only ever paint a line the discount will actually zero: the gun must be one
  // of ours AND the cart must clear $300. Painting an unqualified gun FREE is
  // how the retired 6-tube trigger lied to customers.
  function markFreeGunInDOM(cart) {
    try {
      if (!cart || !Array.isArray(cart.items)) return;
      const gunItem = cart.items.find(isGiftGunLine);
      if (!gunItem) return;
      // Same rule as the bag: the allocation decides, not the threshold.
      if (!lineIsZeroed(gunItem)) {
        unpaintFreeRows(FREE_GUN_HANDLE, 'nbFreeGunMarked');
        return;
      }
      const unitPrice = grossLinePrice(gunItem);
      const rows = document.querySelectorAll('.cart-items__table-row, [data-key]');
      rows.forEach((row) => {
        const link = row.querySelector('a[href*="/products/' + FREE_GUN_HANDLE + '"]');
        if (!link) return;
        if (row.dataset.nbFreeGunMarked === '1') return;
        row.dataset.nbFreeGunMarked = '1';
        row.classList.add('nb-cart-free-bag');

        const priceCell = row.querySelector('.cart-items__price');
        if (priceCell && !priceCell.querySelector('.nb-free-bag-price')) {
          priceCell.dataset.nbOriginal = priceCell.innerHTML;
          priceCell.innerHTML =
            '<div class="nb-free-bag-price">' +
              '<span class="nb-free-bag-free">FREE</span>' +
              '<s class="nb-free-bag-was">' + formatMoney(unitPrice) + '</s>' +
            '</div>';
        }
        const unit = row.querySelector('.cart-items__unit-price-wrapper');
        if (unit && !unit.querySelector('.nb-free-bag-unit')) {
          unit.dataset.nbOriginal = unit.innerHTML;
          unit.innerHTML =
            '<div class="nb-free-bag-unit">' +
              '<span class="nb-free-bag-free">$0.00</span>' +
              '<s class="nb-free-bag-was">' + formatMoney(unitPrice) + '</s>' +
            '</div>';
        }
        const titleBlock = row.querySelector('.cart-items__product-info');
        if (titleBlock && !titleBlock.querySelector('.nb-free-bag-badge')) {
          const badge = document.createElement('span');
          badge.className = 'nb-free-bag-badge';
          badge.textContent = 'FREE GIFT · $300+ ORDER';
          titleBlock.appendChild(badge);
        }
      });
    } catch (e) {
      /* noop — purely visual */
    }
  }

  // Is this line one of our auto-added gift guns? Accepts the legacy prop too,
  // so guns added by the retired 6-tube trigger are still recognised as ours
  // and get reconciled away instead of stranding a $14.99 charge in the cart.
  function isGiftGunLine(i) {
    const isGun =
      i.variant_id === FREE_GUN_VARIANT ||
      i.id === FREE_GUN_VARIANT ||
      (i.handle && i.handle === FREE_GUN_HANDLE);
    if (!isGun) return false;
    const prop = (i.properties || {})._gift;
    return prop === FREE_GUN_PROP || prop === FREE_GUN_PROP_LEGACY;
  }

  // Cart subtotal with our own gift lines excluded, so a gift can never pay for
  // itself or for the next tier up.
  function nonGiftSubtotal(cart) {
    const gross = cart.items_subtotal_price || cart.total_price || 0;
    let giftValue = 0;
    (cart.items || []).forEach((i) => {
      const prop = (i.properties || {})._gift;
      if (!prop) return;
      giftValue += i.line_price || i.final_line_price || 0;
    });
    return Math.max(0, gross - giftValue);
  }

  // Reconcile the auto-added free-gun line against the $300 order-value tier.
  // Exactly one gun, because the backing BXGY has usesPerOrderLimit 1. Handing
  // out a second gun would mean charging for it.
  async function evaluateFreeGun(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;

    let giftLine = -1;
    let giftQty = 0;
    cart.items.forEach((i, idx) => {
      if (isGiftGunLine(i)) {
        giftLine = idx;
        giftQty = i.quantity;
      }
    });

    // Respect an explicit removal: if they took the gun out, do not put it back.
    // Cleared when the cart drops below the threshold so a later requalify works.
    let userRemoved = false;
    try {
      userRemoved = sessionStorage.getItem(STORAGE_USER_REMOVED_GUN) === '1';
    } catch (e) {
      /* sessionStorage unavailable, treat as not removed */
    }

    const qualifies = giftGrants(nonGiftSubtotal(cart)).gun;
    if (!qualifies) {
      try {
        sessionStorage.removeItem(STORAGE_USER_REMOVED_GUN);
      } catch (e) {
        /* noop */
      }
    }

    // A gift gun the engine did not zero is a $14.99 charge the shopper never
    // asked for. Pull it, and record why, so the next evaluation does not just
    // add it straight back.
    const gunLine = giftLine >= 0 ? cart.items[giftLine] : null;
    const gunCharged = !!gunLine && !lineIsZeroed(gunLine);
    if (gunCharged) setGiftsBlocked(cart);

    // Blocked only ever stops us ADDING. Removing on the flag alone could pull a
    // gun the engine really did zero, if the flag were stale.
    const wouldBeAdding = giftQty === 0;
    const desired =
      qualifies && !userRemoved && !gunCharged && !(wouldBeAdding && giftsBlocked(cart))
        ? 1
        : 0;
    if (desired === giftQty) return false;
    if (gunCharged) window.__nbGiftWasBlocked = true;
    try {
      if (giftLine >= 0) {
        // We are pulling or trimming our own gift, so the removal tracker below
        // must not read this as the customer removing it.
        if (desired < giftQty) window.__nbGunJustAutoRemoved = true;
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: giftLine + 1, quantity: desired }),
        });
        return res.ok;
      } else if (desired > 0) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: FREE_GUN_VARIANT, quantity: desired, properties: { _gift: FREE_GUN_PROP } }],
          }),
        });
        return res.ok;
      }
    } catch (e) {
      warn('free gun reconcile failed', e);
    }
    return false;
  }

  function formatMoney(cents) {
    try {
      return '$' + (cents / 100).toFixed(2);
    } catch (e) {
      return '$0.00';
    }
  }

  async function getBagVariantId() {
    try {
      const p = await jget('/products/' + BAG_HANDLE + '.js');
      if (!p || !p.variants || !p.variants.length) return null;
      const v = p.variants.find((x) => x.available) || p.variants[0];
      return v ? v.id : null;
    } catch (e) {
      warn('bag product lookup failed', e);
      return null;
    }
  }

  async function getSiliconeVariantId() {
    try {
      const p = await jget('/products/' + SILICONE_HANDLE + '.js');
      if (!p || !p.variants || !p.variants.length) return null;
      const v = p.variants.find((x) => x.available) || p.variants[0];
      return v ? v.id : null;
    } catch (e) {
      warn('silicone product lookup failed', e);
      return null;
    }
  }

  function cartHasCartridgeGun(cart) {
    const handles = Array.isArray(window.NB_CARTRIDGE_HANDLES)
      ? window.NB_CARTRIDGE_HANDLES
      : [];
    if (!handles.length || !cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((i) => {
      const h = (i.handle && i.handle) || (i.product && i.product.handle) || '';
      return h && handles.indexOf(h) !== -1;
    });
  }

  async function addSilicone(variantId) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: variantId, quantity: 1, properties: { _gift: SILICONE_GIFT_PROP } },
          ],
        }),
      });
      if (!res.ok) {
        warn('add silicone failed', res.status, await res.text());
        return false;
      }
      return true;
    } catch (e) {
      warn('add silicone exception', e);
      return false;
    }
  }

  async function removeAutoSilicone(cart) {
    try {
      const lineIndex = cart.items.findIndex((i) => {
        const isSil =
          (i.handle && i.handle === SILICONE_HANDLE) ||
          (i.product && i.product.handle === SILICONE_HANDLE);
        if (!isSil) return false;
        return (i.properties || {})._gift === SILICONE_GIFT_PROP;
      });
      if (lineIndex < 0) return false;
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: lineIndex + 1, quantity: 0 }),
      });
      return res.ok;
    } catch (e) {
      warn('remove auto silicone failed', e);
      return false;
    }
  }

  // Mark the auto-gift silicone line as FREE in cart DOM. BXGY only applies at
  // checkout, so we paint the line $0 / strikethrough to match the bag.
  function markFreeSiliconeInDOM(cart) {
    try {
      if (!cart || !Array.isArray(cart.items)) return;
      const sil = cart.items.find(
        (i) =>
          ((i.handle && i.handle === SILICONE_HANDLE) ||
            (i.product && i.product.handle === SILICONE_HANDLE)) &&
          (i.properties || {})._gift === SILICONE_GIFT_PROP
      );
      if (!sil) return;
      // The "Free Octogun Silicone with any cartridge gun" BXGY has been
      // deactivated since June 2026, so a legacy cart still carrying this line
      // would have been painted FREE and charged. Nothing adds the property any
      // more, but the allocation check makes the leftover path safe regardless.
      if (!lineIsZeroed(sil)) {
        unpaintFreeRows(SILICONE_HANDLE, 'nbFreeSiliconeMarked');
        return;
      }
      const price = grossLinePrice(sil);

      const rows = document.querySelectorAll('.cart-items__table-row, [data-key]');
      rows.forEach((row) => {
        const link = row.querySelector('a[href*="/products/' + SILICONE_HANDLE + '"]');
        if (!link) return;
        if (row.dataset.nbFreeSiliconeMarked === '1') return;
        row.dataset.nbFreeSiliconeMarked = '1';
        row.classList.add('nb-cart-free-bag');

        const priceCell = row.querySelector('.cart-items__price');
        if (priceCell && !priceCell.querySelector('.nb-free-bag-price')) {
          priceCell.dataset.nbOriginal = priceCell.innerHTML;
          priceCell.innerHTML =
            '<div class="nb-free-bag-price">' +
              '<span class="nb-free-bag-free">FREE</span>' +
              '<s class="nb-free-bag-was">' + formatMoney(price) + '</s>' +
            '</div>';
        }
        const unit = row.querySelector('.cart-items__unit-price-wrapper');
        if (unit && !unit.querySelector('.nb-free-bag-unit')) {
          unit.dataset.nbOriginal = unit.innerHTML;
          unit.innerHTML =
            '<div class="nb-free-bag-unit">' +
              '<span class="nb-free-bag-free">$0.00</span>' +
              '<s class="nb-free-bag-was">' + formatMoney(price) + '</s>' +
            '</div>';
        }
        const titleBlock = row.querySelector('.cart-items__product-info');
        if (titleBlock && !titleBlock.querySelector('.nb-free-bag-badge')) {
          const badge = document.createElement('span');
          badge.className = 'nb-free-bag-badge';
          badge.textContent = 'FREE GIFT · WITH CARTRIDGE GUN';
          titleBlock.appendChild(badge);
        }
      });
    } catch (e) { /* noop */ }
  }

  async function addBag(variantId) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { _gift: 'free-bag-tier' },
            },
          ],
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        warn('add bag failed', res.status, body);
        return false;
      }
      return true;
    } catch (e) {
      warn('add bag exception', e);
      return false;
    }
  }

  // Remove the auto-added bag (when subtotal drops below threshold).
  // Finds the line by product handle + our _gift property so we never
  // remove a bag the user explicitly added themselves.
  async function removeAutoBag(cart) {
    try {
      const lineIndex = cart.items.findIndex((i) => {
        const isBag =
          (i.handle && i.handle === BAG_HANDLE) ||
          (i.product && i.product.handle === BAG_HANDLE);
        if (!isBag) return false;
        const props = i.properties || {};
        return props._gift === 'free-bag-tier';
      });
      if (lineIndex < 0) return false;
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: lineIndex + 1, quantity: 0 }),
      });
      return res.ok;
    } catch (e) {
      warn('remove auto bag failed', e);
      return false;
    }
  }

  // The reward ladder the bar advertises, ascending. Every gift entry MUST have
  // a live BXGY behind it or the cart charges for the gift. Keep in sync with
  // the same list in sections/newborn-cart-progress.liquid.
  //
  // `consumes` marked a gift backed by a BXGY with a dollar minimum, which Shopify
  // consumes out of the cart rather than sharing between discounts, so two such
  // gifts needed the SUM of their thresholds (verified live 6 Aug 2026: at $325 the
  // $300 gun applied and the $35 bag did not, at $350 both did).
  //
  // Both gifts moved into the octogun-promo Function on 18 Aug 2026 and the two
  // BXGYs were deactivated, because a BXGY excludes its buy-side lines from every
  // other product discount and was cancelling the case BOGO. A Function evaluates
  // each reward independently against the same subtotal, so nothing is consumed
  // and every tier is now just its own threshold. If a gift is ever moved back to
  // a BXGY, restore `consumes: true` for it or the cart will add a gift that
  // checkout then charges for.
  const PROGRESS_TIERS = [
    { key: 'ship', cents: SHIP_THRESHOLD, label: 'Free shipping', consumes: false },
    { key: 'bag', cents: BAG_THRESHOLD, label: 'Free tool bag', consumes: false },
    { key: 'gun', cents: GUN_THRESHOLD, label: 'Free 930-GTD', consumes: false },
  ];

  // Subtotal a cart needs before this tier is actually honoured, including the
  // entitlement consumed by every richer gift that applies first. Shopify awards
  // the largest qualifying BXGY first, so a lower gift has to clear its own
  // threshold on top of all the higher ones.
  function tierRequirement(tier) {
    if (!tier.consumes) return tier.cents;
    return PROGRESS_TIERS.reduce(
      (sum, t) => (t.consumes && t.cents >= tier.cents ? sum + t.cents : sum),
      0
    );
  }

  // Which gifts this cart genuinely qualifies for. Mirrors Shopify's own
  // highest-first consumption so the JS never adds a gift the discount engine
  // will refuse to zero.
  function giftGrants(subtotal) {
    const grants = {};
    let remaining = subtotal;
    PROGRESS_TIERS.slice()
      .reverse()
      .forEach((t) => {
        if (!t.consumes) {
          grants[t.key] = subtotal >= t.cents;
          return;
        }
        if (remaining >= t.cents) {
          grants[t.key] = true;
          remaining -= t.cents;
        } else {
          grants[t.key] = false;
        }
      });
    return grants;
  }

  // The next reward they have not earned yet, or null once all are unlocked.
  function nextProgressTier(subtotal) {
    const grants = giftGrants(subtotal);
    return PROGRESS_TIERS.find((t) => !grants[t.key]) || null;
  }

  // Update progress-bar DOM in place when cart changes (no reload).
  // The bar tracks the NEXT unreached reward rather than the whole ladder, so
  // the $25 and $35 tiers stay legible next to the $300 one.
  function renderProgress(cart) {
    const root = document.querySelector('.nb-cart-progress');
    if (!root) return;

    // Gift lines excluded, so a free gift cannot fund the next tier.
    const subtotal = nonGiftSubtotal(cart);
    const grants = giftGrants(subtotal);
    const next = nextProgressTier(subtotal);
    const target = next
      ? tierRequirement(next)
      : tierRequirement(PROGRESS_TIERS[PROGRESS_TIERS.length - 1]);
    const progPct = Math.min(100, (subtotal / target) * 100);

    const fill = root.querySelector('.nb-cart-progress__fill');
    if (fill) fill.style.width = progPct.toFixed(1) + '%';

    const end = root.querySelector('.nb-cart-progress__milestone--end');
    if (end) {
      end.setAttribute('data-hit', next ? 'false' : 'true');
      const endLabel = end.querySelector('.nb-cart-progress__milestone-label');
      if (endLabel) {
        endLabel.textContent = next
          ? next.label
          : PROGRESS_TIERS[PROGRESS_TIERS.length - 1].label;
      }
    }

    const track = root.querySelector('.nb-cart-progress__track');
    if (track) track.setAttribute('aria-valuenow', String(Math.round(progPct)));

    // When a discount code has beaten the gift rewards, the ladder below is not
    // what this cart will actually get. Say so instead of promising gifts the
    // shopper will not receive.
    const blocked = giftRewardsSuppressed(cart);
    root.setAttribute('data-gift-blocked', blocked ? 'true' : 'false');

    const msg = root.querySelector('.nb-cart-progress__message');
    if (!msg) return;

    if (blocked) {
      const codes = applicableCodeKey(cart).split('|').filter(Boolean);
      msg.innerHTML =
        '<strong>' +
        (codes.length ? codes.join(' + ') + ' applied.' : 'Discount applied.') +
        '</strong> A discount code replaces the free-gift offers rather than ' +
        'adding to them, so the gifts are paused on this order. Remove the code ' +
        'to take the gifts instead.';
      return;
    }

    const money = (cents) => '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    const check =
      '<span class="nb-cart-progress__check" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4 10-10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>';

    if (!next) {
      msg.innerHTML =
        check + '<strong>Free shipping, free tool bag and a free 930-GTD unlocked.</strong>';
      appendCodeCaveat(msg, cart);
      return;
    }

    const unlocked = PROGRESS_TIERS.filter((t) => grants[t.key]).map((t) => t.label);
    const away =
      "You're <strong>" + money(tierRequirement(next) - subtotal) + '</strong> away from a ' +
      next.label.replace(/^Free /, 'free ') + '.';

    msg.innerHTML = unlocked.length
      ? check + '<strong>' + unlocked.join(' + ') + ' unlocked.</strong> ' + away
      : away;

    appendCodeCaveat(msg, cart);
  }

  // A code entered at CHECKOUT is invisible to this script — checkout is not our
  // page, so we cannot pull the gift there the way we do in the cart. Say the
  // trade out loud instead, and only to the carts that stand to lose something:
  // the ones already holding one of our gift lines.
  function appendCodeCaveat(msg, cart) {
    if (!msg) return;
    const holdsGift = ((cart || {}).items || []).some(
      (i) => i && i.properties && i.properties._gift
    );
    if (!holdsGift) return;
    const note = document.createElement('small');
    note.className = 'nb-cart-progress__caveat';
    note.textContent =
      'Using a discount code? A code replaces these gift offers rather than adding to them.';
    msg.appendChild(document.createElement('br'));
    msg.appendChild(note);
  }

  // Highest reward reached. Do not use this to decide whether a LOWER reward
  // applies, since 'gun' outranks 'bag'. Compare against the threshold instead.
  function currentTier(subtotal) {
    if (subtotal >= GUN_THRESHOLD) return 'gun';
    if (subtotal >= BAG_THRESHOLD) return 'bag';
    if (subtotal >= SHIP_THRESHOLD) return 'ship';
    return 'none';
  }

  // --- confetti ---
  function burstConfetti(target) {
    const host =
      target ||
      document.querySelector('.nb-cart-progress') ||
      document.querySelector('.nb-cart-hero') ||
      document.body;
    if (!host) return;
    const container = document.createElement('div');
    container.className = 'nb-confetti';
    host.appendChild(container);

    const colors = ['#003087', '#0066ff', '#CC0000', '#ffd84d', '#ffffff', '#66aaff'];
    const count = 80;
    for (let i = 0; i < count; i++) {
      const piece = document.createElement('span');
      piece.className = 'nb-confetti__piece';
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = 6 + Math.random() * 8;
      const left = 10 + Math.random() * 80;
      const duration = 900 + Math.random() * 1200;
      const delay = Math.random() * 200;
      const rotate = Math.random() * 720 - 360;
      const drift = Math.random() * 200 - 100;
      piece.style.cssText =
        'left:' + left + '%;' +
        'background:' + color + ';' +
        'width:' + size + 'px;' +
        'height:' + (size * 0.4) + 'px;' +
        '--rotate:' + rotate + 'deg;' +
        '--drift:' + drift + 'px;' +
        'animation-duration:' + duration + 'ms;' +
        'animation-delay:' + delay + 'ms;';
      container.appendChild(piece);
    }
    setTimeout(() => container.remove(), 2800);
  }

  // --- toast banner ---
  function showBanner(message) {
    const existing = document.querySelector('.nb-cart-banner');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.className = 'nb-cart-banner';
    el.setAttribute('role', 'status');
    el.innerHTML =
      '<span class="nb-cart-banner__icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4 10-10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>' +
      '<span class="nb-cart-banner__text">' + message + '</span>';
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => el.remove(), 400);
    }, 3800);
  }

  // --- main evaluation ---
  let isEvaluating = false;

  async function evaluateCart() {
    if (isEvaluating) return;
    isEvaluating = true;
    try {
      let cart = await getCart();
      updateOctogunBodyClass(cart);
      markFreeBagInDOM(cart);
      markFreeGunInDOM(cart);

      // NOTE: The "Free Octogun Silicone with any cartridge gun" auto-gift was
      // retired June 2026 (promotion ended; its BXGY discount was deactivated).

      // --- Free Model 930-GTD per 6 loose single Octogun Silicone tubes ---
      const FG_LOCK = 'nb_freegun_adding';
      const fgLockTs = parseInt(sessionStorage.getItem(FG_LOCK) || '0', 10);
      if (Date.now() - fgLockTs >= 4000) {
        sessionStorage.setItem(FG_LOCK, Date.now().toString());
        const fgChanged = await evaluateFreeGun(cart);
        sessionStorage.removeItem(FG_LOCK);
        if (fgChanged) {
          cart = await getCart();
          ['cart:refresh', 'cart:updated', 'cart:changed'].forEach((ev) => {
            document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
          });
          markFreeGunInDOM(cart);
          if (window.__nbGiftWasBlocked) {
            window.__nbGiftWasBlocked = false;
            showBanner(
              'Your discount code is worth more than the free 930-GTD, so we removed the gun. Codes and gift offers cannot be combined.'
            );
          }
          if (isCartPage()) setTimeout(() => window.location.reload(), 500);
        }
      }

      let subtotal = cart.items_subtotal_price || cart.total_price || 0;
      let tier = currentTier(subtotal);
      const prevTier = sessionStorage.getItem(STORAGE_LAST_TIER) || 'none';

      // Bag branches below test this, not `tier`, because `tier` names only the
      // HIGHEST reward reached, so a $300 cart reports 'gun' and any
      // `tier === 'bag'` test would wrongly pull the bag. The old second reason,
      // that the bag needed $335 whenever the gun was in play, went away with the
      // BXGYs on 18 Aug 2026: the Function grants each gift on its own threshold.
      let qualifiesForBag = giftGrants(nonGiftSubtotal(cart)).bag;

      // Identify the auto-added bag vs a user-added one
      const bagLine = cart.items.find(
        (i) =>
          ((i.handle && i.handle === BAG_HANDLE) ||
            (i.product && i.product.handle === BAG_HANDLE))
      );
      const hasBag = !!bagLine;
      const isAutoBag =
        bagLine && bagLine.properties && bagLine.properties._gift === 'free-bag-tier';

      log('evaluate', { subtotal, tier, prevTier, hasBag, isAutoBag });

      // Live-update the progress bar DOM on every evaluation so removing
      // items instantly reduces the fill / milestone hit states.
      renderProgress(cart);

      // Track user removal (only count as removal if it was a bag THEY added,
      // not if we auto-removed our own gift below)
      if (window.__nbCartHadBag && !hasBag && !window.__nbCartJustAutoRemoved) {
        sessionStorage.setItem(STORAGE_USER_REMOVED, '1');
        log('user removed bag');
      }
      window.__nbCartJustAutoRemoved = false;
      window.__nbCartHadBag = hasBag;

      // Same removal tracking for the gift gun, so taking it out sticks for the
      // session instead of us silently re-adding it on the next evaluation.
      const hasGiftGun = cart.items.some(isGiftGunLine);
      if (window.__nbCartHadGun && !hasGiftGun && !window.__nbGunJustAutoRemoved) {
        sessionStorage.setItem(STORAGE_USER_REMOVED_GUN, '1');
        log('user removed gift gun');
      }
      window.__nbGunJustAutoRemoved = false;
      window.__nbCartHadGun = hasGiftGun;

      // Pull a gift bag WE added that the discount engine did not zero. This is
      // the OCTO40 case: a code that cannot combine with product discounts
      // suppresses the whole octogun-promo function, so the bag reward vanishes
      // and the shopper is charged $9.99 for a bag they never chose. Their order
      // total is still the best available, so the fix is to remove the unasked-
      // for gift rather than to fight the code.
      if (hasBag && isAutoBag && !lineIsZeroed(bagLine)) {
        setGiftsBlocked(cart);
        const pulled = await removeAutoBag(cart);
        if (pulled) {
          window.__nbCartJustAutoRemoved = true;
          showBanner(
            'Your discount code is worth more than the free tool bag, so we removed the bag. Codes and gift offers cannot be combined.'
          );
          cart = await getCart();
          renderProgress(cart);
          const blockedEvt = new Event('cart:update', { bubbles: true });
          blockedEvt.detail = { resource: null, sourceId: 'nb-cart-gift-blocked', data: { source: 'nb-cart-gift-blocked' } };
          document.dispatchEvent(blockedEvt);
          ['cart:refresh', 'cart:updated', 'cart:changed'].forEach((ev) => {
            document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
          });
          if (isCartPage()) setTimeout(() => window.location.reload(), 600);
          isEvaluating = false;
          return;
        }
      }

      // Auto-REMOVE our gift bag when subtotal drops below the threshold.
      // (They no longer qualify — pull the freebie, let Shopify recalc.)
      if (!qualifiesForBag && hasBag && isAutoBag) {
        const ok = await removeAutoBag(cart);
        if (ok) {
          window.__nbCartJustAutoRemoved = true;
          showBanner(
            'Free bag removed (order no longer qualifies).'
          );
          // Refetch + re-render after removal
          cart = await getCart();
          subtotal = cart.items_subtotal_price || cart.total_price || 0;
          tier = currentTier(subtotal);
          qualifiesForBag = giftGrants(nonGiftSubtotal(cart)).bag;
          renderProgress(cart);
          const removeEvt = new Event('cart:update', { bubbles: true });
          removeEvt.detail = { resource: null, sourceId: 'nb-cart-auto-bag-remove', data: { source: 'nb-cart-auto-bag-remove' } };
          document.dispatchEvent(removeEvt);
          ['cart:refresh', 'cart:updated', 'cart:changed'].forEach((ev) => {
            document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
          });
          if (isCartPage()) {
            setTimeout(() => window.location.reload(), 600);
          }
          sessionStorage.setItem(STORAGE_LAST_TIER, tier);
          isEvaluating = false;
          return;
        }
      }

      // Safe dedupe: if cart has >1 bag, remove ONE extra (the highest-index
      // non-keep line). Doing a single surgical remove avoids any line-index
      // drift issue. If there's still a dupe, the next evaluate cleans it up.
      const bagLines = cart.items
        .map((i, idx) => ({ item: i, line: idx + 1 }))
        .filter(
          (x) =>
            (x.item.handle && x.item.handle === BAG_HANDLE) ||
            (x.item.product && x.item.product.handle === BAG_HANDLE)
        );
      if (bagLines.length > 1) {
        const giftLine = bagLines.find(
          (x) => (x.item.properties || {})._gift === 'free-bag-tier'
        );
        const keep = giftLine || bagLines[0];
        const extras = bagLines
          .filter((x) => x.line !== keep.line)
          .sort((a, b) => b.line - a.line); // highest first
        const target = extras[0];
        if (target) {
          try {
            const res = await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ line: target.line, quantity: 0 }),
            });
            if (res.ok) {
              cart = await getCart();
              renderProgress(cart);
              const dedupeEvt = new Event('cart:update', { bubbles: true });
              dedupeEvt.detail = { resource: null, sourceId: 'nb-cart-dedupe', data: { source: 'nb-cart-dedupe' } };
              document.dispatchEvent(dedupeEvt);
              ['cart:refresh', 'cart:updated', 'cart:changed'].forEach((ev) => {
                document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
              });
              if (isCartPage()) {
                setTimeout(() => window.location.reload(), 400);
              }
            }
          } catch (e) {
            warn('dedupe remove failed', e);
          }
          isEvaluating = false;
          return;
        }
      }

      // Auto-add bag when threshold crossed (works on ANY page, not just /cart)
      const BAG_LOCK = 'nb_bag_adding';
      const lockTs = parseInt(sessionStorage.getItem(BAG_LOCK) || '0', 10);
      const lockFresh = Date.now() - lockTs < 5000;
      // giftsBlocked: a code on this cart already beat the gift rewards once.
      // Adding the bag again would just re-charge for it. The flag is keyed to
      // the applicable codes, so removing the code un-blocks it automatically.
      if (
        qualifiesForBag &&
        !hasBag &&
        !giftRewardsSuppressed(cart) &&
        !sessionStorage.getItem(STORAGE_USER_REMOVED) &&
        !lockFresh
      ) {
        // Cross-call mutex: mark before any awaits so another evaluateCart
        // that sneaks past isEvaluating cannot double-add.
        sessionStorage.setItem(BAG_LOCK, Date.now().toString());
        // Race protection: re-fetch cart right before adding. A prior evaluate
        // may have already added the bag while we were in-flight fetching
        // product data — never pile up a second line.
        const fresh = await getCart();
        if (fresh.items.some((i) => (i.handle || (i.product && i.product.handle)) === BAG_HANDLE)) {
          sessionStorage.removeItem(BAG_LOCK);
          isEvaluating = false;
          return;
        }
        const variantId = await getBagVariantId();
        if (!variantId) {
          warn('could not resolve bag variant id, abort');
          sessionStorage.removeItem(BAG_LOCK);
          isEvaluating = false;
          return;
        }
        const ok = await addBag(variantId);
        if (ok) {
          sessionStorage.setItem(STORAGE_LAST_TIER, tier);
          burstConfetti();
          showBanner('Free bag added to your order!');
          // If on cart page, reload so Horizon's native cart re-renders.
          // Elsewhere, fire cart events so the drawer picks up the new line.
          if (isCartPage()) {
            setTimeout(() => window.location.reload(), 900);
          } else {
            // Horizon theme's cart drawer + cart-items listen for 'cart:update'
            const updateEvt = new Event('cart:update', { bubbles: true });
            updateEvt.detail = { resource: null, sourceId: 'nb-cart-auto-bag', data: { source: 'nb-cart-auto-bag' } };
            document.dispatchEvent(updateEvt);
            ['cart:refresh', 'cart:updated', 'cart:changed'].forEach((ev) => {
              document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
            });
          }
          return;
        }
      }

      // Tier-change confetti (not bag, which already fired above)
      if (tier !== prevTier) {
        if (prevTier === 'none' && tier === 'ship') {
          burstConfetti();
          showBanner('Free shipping unlocked!');
        }
        sessionStorage.setItem(STORAGE_LAST_TIER, tier);
      }

      // Viewing-the-cart confetti: if user lands on /cart and bag is already
      // unlocked & present, pop a celebratory burst once per page visit.
      if (isCartPage() && qualifiesForBag && hasBag && !window.__nbCartViewBurst) {
        window.__nbCartViewBurst = true;
        setTimeout(() => {
          burstConfetti();
        }, 350);
      }
    } catch (e) {
      warn('evaluate failed', e);
    } finally {
      isEvaluating = false;
    }
  }

  // --- wiring ---
  function isCartPage() {
    return (
      document.body.classList.contains('template-cart') ||
      !!document.querySelector('.template-cart') ||
      location.pathname.replace(/\/$/, '') === '/cart'
    );
  }

  function wire() {
    // Evaluate on EVERY page load (so the bag auto-adds even if the user
    // goes drawer -> checkout without visiting /cart).
    evaluateCart();

    // Also intercept any checkout buttons: run one last evaluation before
    // the browser navigates to /checkout. If the bag is missing and they
    // qualify, we add it and briefly delay the checkout click.
    document.addEventListener(
      'click',
      function (e) {
        const btn =
          e.target &&
          e.target.closest &&
          e.target.closest(
            'button[name="checkout"], a[href*="/checkout"], button[href*="/checkout"]'
          );
        if (!btn) return;
        if (btn.dataset.nbCheckoutChecked === '1') return; // prevent loop
        if (sessionStorage.getItem(STORAGE_USER_REMOVED)) return;

        // Non-blocking peek: if subtotal already >= threshold, ensure bag is in
        // cart before allowing checkout to proceed.
        e.preventDefault();
        e.stopPropagation();
        btn.disabled = true;
        (async () => {
          try {
            await evaluateCart();
          } finally {
            btn.dataset.nbCheckoutChecked = '1';
            btn.disabled = false;
            // Small delay to let /cart/add.js finish before re-clicking
            setTimeout(() => btn.click(), 400);
          }
        })();
      },
      true
    );

    // Handle inline add from related grid (data-related-add forms)
    document.addEventListener('submit', function (e) {
      const form = e.target && e.target.closest && e.target.closest('[data-related-add]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;
      const fd = new FormData(form);
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
        .then((r) => r.json())
        .then(() => {
          if (btn) btn.innerHTML = 'Added';
          setTimeout(() => window.location.reload(), 350);
        })
        .catch((err) => {
          warn('related add failed', err);
          if (btn) btn.disabled = false;
        });
    });

    // React to Horizon's native cart events (atc, qty change, remove) on ANY page
    const events = ['cart:refresh', 'cart:updated', 'cart:changed', 'cart-update'];
    events.forEach((ev) =>
      document.addEventListener(ev, () => {
        evaluateCart();
        // Horizon re-renders cart rows async on quantity changes. Re-apply
        // the free-bag visual markers twice after a short delay so we catch
        // the new DOM.
        setTimeout(() => getCart().then((c) => { markFreeBagInDOM(c); markFreeSiliconeInDOM(c); markFreeGunInDOM(c); }).catch(() => {}), 350);
        setTimeout(() => getCart().then((c) => { markFreeBagInDOM(c); markFreeSiliconeInDOM(c); markFreeGunInDOM(c); }).catch(() => {}), 900);
      })
    );

    // Re-apply markings when the cart DOM is swapped in place by Horizon's
    // component-cart-items.js (morph / section re-render on qty change).
    const cartContainer = document.querySelector('cart-items-component, cart-drawer-component');
    if (cartContainer && 'MutationObserver' in window) {
      let t;
      const obs = new MutationObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => {
          getCart().then((c) => { markFreeBagInDOM(c); markFreeSiliconeInDOM(c); markFreeGunInDOM(c); }).catch(() => {});
        }, 100);
      });
      obs.observe(cartContainer, { childList: true, subtree: true });
    }

    // Re-evaluate when tab regains focus
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        evaluateCart();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();
